import {
    RiDeleteBinLine, RiEdit2Line,
    RiInputField,
    RiInputMethodLine,
} from "@remixicon/react";
import React, {
    ReactNode,
    useMemo,
} from "react";
import {
    FormField,
    FormFieldType,
    FormType,
    GetFormDataDocument,
    GetRequestTypesDocument,
    useDeleteFormFieldMutation,
    useGetRequestTypesQuery,
    UserGroup,
} from "../../../../__generated__/api";
import useRouting from "../../../../contexts/Routing";

import { ReactComponent as GroupsList } from "../../../../assets/icons/groups-list.svg";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import FieldType, { CUSTOM_FIELD_PROPS } from "../../../../components/pickers/field-type/FieldType";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Badge from "../../../../components/theme/badge/Badge";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import { useNewFieldDrawer } from "../custom-field/CustomFieldV3";

interface CustomFieldWithGroups extends FormField {
    groups: UserGroup[]
}
const TableContainer = createTableContainer<CustomFieldWithGroups>();

export default function RequestTypes(): ReactNode {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        variables: {
            type: FormType.Default,
            shouldIncludeOptions: true,
        },
    });

    const formFields = useMemo(() => {
        const groupMap = data?.userGroups.toIdMap("id") ?? {};
        return data?.formFields.map(form => ({
            ...form,
            groups: form.userGroupIds?.mapFilter(id => groupMap[id]) ?? [],
        })) ?? [];
    }, [data?.formFields]);

    const { Paths, createHistoryPush } = useRouting();

    const [deleteFormField] = useDeleteFormFieldMutation();

    const { element, setNew, setEdit } = useNewFieldDrawer({
        onSave: field => {
            // save
        },
    });

    const { element: deleteElement, setDeleting } = useDeleteConfirm<FormField>(
        async field => {
            await deleteFormField({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    {
                        query: GetRequestTypesDocument,
                        variables: {
                            type: FormType.Default,
                            shouldIncludeOptions: true,
                        },
                    },
                ],
                variables: {
                    id: field.id,
                },
            });
        },
        item => item.name,
        "Remove Field",
        "If you delete the field, it will be removed from all the forms where it is used.",

    );

    return (
        <>
            <SettingsContainer.Section
                sectionKey="custom-fields"
                title="Custom Fields"
                // counter={(formFields.length ?? 0).toString()}
                shouldDisplayTitle={false}
            >
                <SettingsContainer.Setting
                    title="Custom Fields"
                    description="Custom fields allow you to store and request information from users in a structured way.
                    By creating a custom field and adding it to a request type, any user creating a ticket with that
                    request type is required to fill in the custom field based on the type of field. If the custom
                    field is just text, the user will be required to enter text. If the field is a dropdown, the user
                    will be required to select from a set of provided options."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isFullWidth
                >
                    <TableContainer data={formFields as CustomFieldWithGroups[] ?? []} isLoading={isLoading} error={error}>
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="Custom fields allow you to store and request information from users in a structured way.
                    By creating a custom field and adding it to a request type, any user creating a ticket with that
                    request type is required to fill in the custom field based on the type of field. If the custom
                    field is just text, the user will be required to enter text. If the field is a dropdown, the user
                    will be required to select from a set of provided options."
                                title="Create an Request Type"
                                icon={<RiInputField />}
                                onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                    setting: "request-fields",
                                    id: "new",
                                })}
                                buttonText="Add Request Type"
                            />
                        </TableContainer.Empty>
                        <TableContainer.TableControls>

                            <TableContainer.TableControls.Search
                                search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                                    || item.description?.toLocaleLowerCase().includes(query)
                                                    || item.groups.some(group => group.name?.toLocaleLowerCase().includes(query))}
                            />
                            <TableContainer.TableControls.Seperator />

                            <TableContainer.TableControls.Filters />

                            <TableContainer.TableControls.Filter
                                defaultValue={[] as string[]}
                                deserialize={TableContainer.TableControls.Filter.Serializers.deserializeArray}
                                serialize={TableContainer.TableControls.Filter.Serializers.serializeArray}
                                filterKey="groups"
                                onFilter={(item, selected) => selected.isEmpty
                                    || selected.union(item.userGroupIds as string[]).isNotEmpty}
                            >
                                {(currentValue, setValue) => (
                                    <UserGroups
                                        isMulti
                                        icon={<GroupsList />}
                                        onSelected={setValue}
                                        value={currentValue}
                                        shouldShowDropdown
                                        toggleText="User Groups"
                                    />
                                )}

                            </TableContainer.TableControls.Filter>

                            <TableContainer.TableControls.Filter
                                defaultValue={[] as FormFieldType[]}
                                deserialize={TableContainer.TableControls.Filter.Serializers.deserializeArray}
                                serialize={TableContainer.TableControls.Filter.Serializers.serializeArray}
                                filterKey="fieldType"
                                onFilter={(item, selected) => selected.includes(item.type)}
                            >
                                {(currentValue, setValue) => (
                                    <FieldType
                                        isMulti
                                        icon={<RiInputMethodLine />}
                                        onChange={setValue}
                                        value={currentValue}
                                        shouldShowDropdown
                                        toggleText="Field Types"
                                    />
                                )}

                            </TableContainer.TableControls.Filter>
                            <TableContainer.TableControls.SortBy />
                            <TableContainer.TableControls.Create
                                text="Add Custom Field"
                                onClick={() => setNew()}
                            />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="name"
                                title="Field Name"
                                sortBy={item => item.name?.toLowerCase()}
                            >
                                {item => item.name}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="description"
                                title="Description"
                                sortBy={item => item.description?.toLowerCase()}
                            >
                                {item => (item.description
                                    ? <HtmlRenderer html={item.description} stripFormatting="all" />
                                    : "")}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="type"
                                title="Field Type"
                                sortBy={item => item.type?.toLowerCase()}
                            >
                                {item => CUSTOM_FIELD_PROPS[item.type ?? FormFieldType.Text].text}
                            </TableContainer.TableValue>

                            <TableContainer.TableValue
                                columnKey="channelTags"
                                title="Channel Groups"
                                sortBy={item => item}
                            >
                                {item => item.groups?.map(group => (
                                    <Badge variant="primary" className="m3e-1" key={group.id}>
                                        {group.name}
                                    </Badge>
                                ))}
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<RiEdit2Line />}
                                altText="Edit Custom Field"
                                onClick={setEdit}
                            />
                            <TableContainer.Action
                                icon={<RiDeleteBinLine />}
                                altText="Delete Request Type"
                                onClick={setDeleting}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
            {element}
            {deleteElement}
        </>
    );
}
